import Link from 'next/link';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { createSanitizedHtmlObject } from 'shared/utils/createSanitizedHtml';
import { GRIDS_WIDTH } from 'search/styles/SearchStyles';
import { InlineModal } from 'shared/components/InlineModal';
import { variableFontMedium, variableFontRegular } from 'styles/theme';
import { showOnDesktopOnly, showOnMobileOnly } from 'styles/utils/responsiveness';
import { t } from 'localization/utils/t';
import { localizeUrl } from 'localization/utils/localizeUrl';

const TP = 'shared.components.Grid.GridHeader';

const SearchClearFilters = dynamic(() => import('search/components/searchGrid/SearchClearFilters'));

interface IGridHeader {
  className?: string;
  count?: number;
  description: string;
  hasLink?: boolean;
  hideMetadata?: boolean;
  isHeaderVisible?: boolean;
  isSearchPath: boolean;
  isStickyAndSuperCompressed: boolean;
  isTimelinePath?: boolean;
  linkDestination?: string;
  linkTitle?: string;
  resultIds?: Array<string>;
  showBrandStory?: () => void;
  title: string;
}

export const GridHeader = ({
  className,
  count,
  description,
  hasLink,
  hideMetadata = false,
  isSearchPath,
  isStickyAndSuperCompressed,
  isTimelinePath,
  linkDestination,
  linkTitle,
  resultIds,
  title,
}: IGridHeader) => {
  const { locale } = useRouter();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  useEffect(() => {
    const handleClicks = () => {
      if (isModalVisible) {
        setIsModalVisible(false);
      }
    };

    window.addEventListener('click', handleClicks);
    return () => {
      window.removeEventListener('click', handleClicks);
    };
  });

  return (
    <GridHeader.Container
      className={className}
      $isStickyAndSuperCompressed={isStickyAndSuperCompressed}
      $isTimelinePath={isTimelinePath}
      $isBrandStoryHeader={hideMetadata}
    >
      <GridHeader.Aside />
      {!hideMetadata && (
        <GridHeader.InnerContainer
          id="header-inner-container"
          $hasDescription={description && description?.length > 0}
          $isStickyAndSuperCompressed={isStickyAndSuperCompressed}
        >
          <GridHeader.LeftData>
            <GridHeader.Title data-qa="grid_header_title">{title}</GridHeader.Title>
            {(description || (hasLink && linkTitle && linkDestination)) && (
              <GridHeader.Description
                id="grid-header-description"
                data-qa="grid_header_description"
                $isStickyAndSuperCompressed={isStickyAndSuperCompressed}
                $hasLink={hasLink}
                onClick={() => isStickyAndSuperCompressed && setIsModalVisible(true)}
              >
                {description && !hideMetadata && (
                  <div
                    dangerouslySetInnerHTML={createSanitizedHtmlObject(
                      localizeUrl(description, locale),
                    )}
                  />
                )}
                {hasLink && !isStickyAndSuperCompressed && (
                  <Link href={linkDestination} passHref legacyBehavior>
                    <GridHeader.RelevantLink data-qa="grid_header_description_link" id="has-link">
                      {linkTitle}
                    </GridHeader.RelevantLink>
                  </Link>
                )}
              </GridHeader.Description>
            )}
            {isModalVisible && isStickyAndSuperCompressed && (
              <GridHeader.Modal data-qa="grid_header_description_modal" isVisible={isModalVisible}>
                {description && (
                  <div
                    dangerouslySetInnerHTML={createSanitizedHtmlObject(
                      localizeUrl(description, locale),
                    )}
                  />
                )}
                {hasLink && (
                  <Link href={linkDestination} passHref legacyBehavior>
                    <GridHeader.RelevantLink data-qa="grid_header_description_link" id="has-link">
                      {linkTitle}
                    </GridHeader.RelevantLink>
                  </Link>
                )}
              </GridHeader.Modal>
            )}
          </GridHeader.LeftData>
          <GridHeader.RightData data-qa="results_count">
            {isStickyAndSuperCompressed && !isSearchPath && description && (
              <GridHeader.StoryButton
                data-qa="details_button"
                onClick={() => isStickyAndSuperCompressed && setIsModalVisible(true)}
              >
                {t(`${TP}.details`, 'Details')}
              </GridHeader.StoryButton>
            )}
            {typeof count === 'number' &&
              (count === 10000
                ? t(`${TP}.maxCount`, '10,000+ Items')
                : t(`${TP}.itemsCount`, '{numItems, plural, =1 {1 Item} other {# Items}}', {
                    numItems: count,
                  }))}
            {isStickyAndSuperCompressed && (
              <GridHeader.SearchClearFiltersWrapper>
                <SearchClearFilters resultIds={resultIds} />
              </GridHeader.SearchClearFiltersWrapper>
            )}
          </GridHeader.RightData>
        </GridHeader.InnerContainer>
      )}
      <GridHeader.Aside />
    </GridHeader.Container>
  );
};

GridHeader.Container = styled.div<{
  $isBrandStoryHeader: boolean;
  $isStickyAndSuperCompressed: boolean;
  $isTimelinePath: boolean;
}>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  padding: 160px 16px 320px;
  width: 100%;
  z-index: 22;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;

  ${({ $isStickyAndSuperCompressed }) =>
    $isStickyAndSuperCompressed && `padding: 0px 0px 16px 0px; `};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
    width: 100%;
    padding: 160px 0 320px;

    ${({ $isStickyAndSuperCompressed, $isTimelinePath }) =>
      $isStickyAndSuperCompressed &&
      `position: relative; padding: 32px 0px ${$isTimelinePath ? `18px` : `32px`};`}

    ${({ $isBrandStoryHeader }) => $isBrandStoryHeader && `padding: 25px; `};
  }
`;

GridHeader.InnerContainer = styled.div<{
  $isStickyAndSuperCompressed: boolean;
  $hasDescription: boolean;
}>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${({ $hasDescription, $isStickyAndSuperCompressed }) =>
    !$isStickyAndSuperCompressed && $hasDescription ? `min-height: 14px;` : `height: 14px;`}

  ${({ $isStickyAndSuperCompressed }) => $isStickyAndSuperCompressed && `padding: 0px 16px;`};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    align-items: center;
    min-height: 14px;
    flex-direction: row;
    width: ${GRIDS_WIDTH};
    ${({ $isStickyAndSuperCompressed }) => $isStickyAndSuperCompressed && `padding: 0px;`};

    ${({ $isStickyAndSuperCompressed, $hasDescription }) =>
      $isStickyAndSuperCompressed && $hasDescription && `min-height: 28px;`}
  }
`;

GridHeader.LeftData = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
  }
`;

GridHeader.Title = styled.h1`
  margin-right: 32px;
  margin-bottom: 32px;
  display: flex;
  align-items: flex-start;
  font-weight: 600;
  text-transform: uppercase;
  ${variableFontMedium({ size: '11.5px', height: '14px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-bottom: 0;
    margin-top: 0;
  }
`;

GridHeader.Description = styled.div<{
  $isStickyAndSuperCompressed: boolean;
  $hasLink: boolean;
}>`
  align-items: flex-start;
  flex-direction: column;
  max-width: 272px;
  font-weight: 400;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  display: block;

  ${({ $isStickyAndSuperCompressed }: { $isStickyAndSuperCompressed: boolean }) =>
    $isStickyAndSuperCompressed &&
    `
    position: relative;
    max-height: 28px;
    display: none;

    &:hover {
      text-decoration: underline;
      cursor: pointer;
    }

    div {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box !important;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  `};

  a {
    ${({ $hasLink }) =>
      $hasLink ? `text-decoration: none !important;` : 'text-decoration: underline'}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-width: 418px;
    display: flex;

    a {
      text-decoration: underline;
    }
  }
`;

GridHeader.RelevantLink = styled.a`
  margin-top: 16px;
  font-weight: 400;
  padding-bottom: 1px;
  width: 156px;
  display: block;

  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    &:hover {
      border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
    }
  }
`;

GridHeader.RightData = styled.div`
  font-weight: 400;
  text-transform: capitalize;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  align-self: flex-start;
`;

GridHeader.StoryButton = styled.button`
  border: none;
  background: none;
  margin-right: 8px;
  outline: none;
  padding: 0;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  &:hover {
    border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

GridHeader.Modal = styled(InlineModal)`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 1px solid ${({ theme }) => theme.navigation.borderColor};
  color: ${({ theme }) => theme.navigation.textColor};
  position: absolute;
  font-weight: 400;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  a {
    text-decoration: underline;
  }

  #has-link {
    &:hover {
      border: none;
    }
  }
`;

GridHeader.Aside = styled.aside`
  ${showOnDesktopOnly`
    width: calc((100% - ${GRIDS_WIDTH})/2);
    display: block;
    text-align: right;
    background-color: ${({ theme }) => theme.navigation.backgroundColor};
  `}
`;

GridHeader.ClickableOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100px;
  height: 100px;
  z-index: 100;
`;

GridHeader.SearchClearFiltersWrapper = styled.span`
  ${showOnMobileOnly``};
`;
